import React, { useState, useEffect, useCallback } from 'react';
import PanelLayout from '../layouts/PanelLayout';
import { contactService } from '../../services/contactService';
import Loader from '../../components/Loader';
import DateTimeDisplay from '../../components/common/DateTimeDisplay';
import Pagination from '../../components/common/Pagination';
import ActionMenu from '../../components/common/ActionMenu';
import ConfirmModal from '../../components/common/ConfirmModal';
import Modal from '../../components/common/Modal';
import Button from '../../components/common/Button';
import SearchableSelect from '../../components/common/SearchableSelect';
import toast from 'react-hot-toast';

const STATUS_OPTIONS = [
   { id: 'NEW', name: 'New' },
   { id: 'CONTACTED', name: 'Contacted' },
   { id: 'WON', name: 'Won' },
   { id: 'LOST', name: 'Lost' },
];

const STATUS_STYLES = {
   NEW: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
   CONTACTED: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
   WON: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
   LOST: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
};

const TYPE_LABELS = { GENERAL: 'General', TOOL_REQUEST: 'Tool request', PROJECT: 'Project' };

const ContactMessages = () => {
   const [messages, setMessages] = useState([]);
   const [loading, setLoading] = useState(true);
   const [page, setPage] = useState(1);
   const [totalPages, setTotalPages] = useState(1);
   const [total, setTotal] = useState(0);
   const [selectedId, setSelectedId] = useState(null);
   const [deletingId, setDeletingId] = useState(null);
   const [deleteTarget, setDeleteTarget] = useState(null);

   const selected = messages.find((m) => m.id === selectedId) || null;

   const fetchMessages = useCallback(async () => {
      try {
         setLoading(true);
         const response = await contactService.getAll({ page, limit: 10 });
         setMessages(response.data.data.contacts);
         setTotalPages(response.data.data.totalPages);
         setTotal(response.data.data.total);
      } catch (error) {
         console.error('Error fetching contact messages:', error);
         toast.error('Failed to load messages');
      } finally {
         setLoading(false);
      }
   }, [page]);

   useEffect(() => {
      fetchMessages();
   }, [fetchMessages]);

   const handleDelete = async () => {
      if (!deleteTarget) return;
      try {
         setDeletingId(deleteTarget.id);
         await contactService.delete(deleteTarget.id);
         toast.success('Message deleted');
         setDeleteTarget(null);
         setSelectedId(null);
         fetchMessages();
      } catch (error) {
         console.error('Error deleting message:', error);
         toast.error('Failed to delete message');
      } finally {
         setDeletingId(null);
      }
   };

   const handleStatusChange = async (id, status) => {
      try {
         await contactService.updateStatus(id, status);
         setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, status } : m)));
         toast.success('Status updated');
      } catch (error) {
         console.error('Error updating status:', error);
         toast.error('Failed to update status');
      }
   };

   if (loading && messages.length === 0) {
      return (
         <PanelLayout>
            <Loader />
         </PanelLayout>
      );
   }

   return (
      <PanelLayout>
         <div className="space-y-6">
            {/* Header */}
            <div>
               <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                  Contact Messages
               </h1>
               <p className="mt-1 text-gray-600 dark:text-gray-400">
                  {total} message{total !== 1 ? 's' : ''} total
               </p>
            </div>

            {/* Table */}
            <div className="rounded-lg bg-white dark:bg-dark-secondary shadow-md overflow-hidden">
               <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                     <thead className="bg-gray-50 dark:bg-gray-800">
                        <tr>
                           <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              Type
                           </th>
                           <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              Status
                           </th>
                           <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              Email
                           </th>
                           <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              Contact
                           </th>
                           <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              Message
                           </th>
                           <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              Date
                           </th>
                           <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              Actions
                           </th>
                        </tr>
                     </thead>
                     <tbody className="bg-white dark:bg-dark-secondary divide-y divide-gray-200 dark:divide-gray-700">
                        {loading ? (
                           <tr>
                              <td
                                 colSpan="7"
                                 className="px-6 py-8 text-center text-gray-500 dark:text-gray-400"
                              >
                                 Loading...
                              </td>
                           </tr>
                        ) : messages.length === 0 ? (
                           <tr>
                              <td
                                 colSpan="7"
                                 className="px-6 py-8 text-center text-gray-500 dark:text-gray-400"
                              >
                                 No messages yet
                              </td>
                           </tr>
                        ) : (
                           messages.map((msg) => (
                              <tr
                                 key={msg.id}
                                 className="hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                                 onClick={() => setSelectedId(msg.id)}
                              >
                                 <td className="px-6 py-4 whitespace-nowrap">
                                    <span
                                       className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                                          msg.type === 'TOOL_REQUEST'
                                             ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
                                             : msg.type === 'PROJECT'
                                               ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                                               : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                                       }`}
                                    >
                                       {msg.type === 'TOOL_REQUEST'
                                          ? 'Tool request'
                                          : msg.type === 'PROJECT'
                                            ? 'Project'
                                            : 'General'}
                                    </span>
                                 </td>
                                 <td className="px-6 py-4 whitespace-nowrap">
                                    <span
                                       className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[msg.status] || STATUS_STYLES.NEW}`}
                                    >
                                       {STATUS_OPTIONS.find((o) => o.id === msg.status)?.name ||
                                          'New'}
                                    </span>
                                 </td>
                                 <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                       {msg.email}
                                    </div>
                                 </td>
                                 <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                    {msg.phone || (
                                       <span className="text-gray-300 dark:text-gray-600">N/A</span>
                                    )}
                                 </td>
                                 <td className="px-6 py-4">
                                    <p className="text-sm text-gray-700 dark:text-gray-300 truncate max-w-xs">
                                       {msg.message}
                                    </p>
                                 </td>
                                 <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                    <DateTimeDisplay dateStr={msg.createdAt} />
                                 </td>
                                 <td
                                    className="px-6 py-4 whitespace-nowrap"
                                    onClick={(e) => e.stopPropagation()}
                                 >
                                    <ActionMenu
                                       items={[
                                          {
                                             label: 'Delete',
                                             variant: 'danger',
                                             onClick: () => setDeleteTarget(msg),
                                          },
                                       ]}
                                    />
                                 </td>
                              </tr>
                           ))
                        )}
                     </tbody>
                  </table>
               </div>
            </div>

            <Pagination
               page={page}
               totalPages={totalPages}
               total={total}
               limit={10}
               onPageChange={setPage}
            />
         </div>

         <Modal
            isOpen={!!selected}
            onClose={() => setSelectedId(null)}
            title="Message Details"
            maxWidth="max-w-2xl"
            allowOverflow
         >
            {selected && (
               <div className="space-y-4">
                  <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                     {[
                        ['Type', TYPE_LABELS[selected.type] || 'General'],
                        ['Email', selected.email],
                        ['Phone', selected.phone],
                        ['Received', new Date(selected.createdAt).toLocaleString()],
                        ...(selected.type === 'PROJECT'
                           ? [
                                ['Project type', selected.projectType],
                                ['Budget', selected.budget],
                                ['Timeline', selected.timeline],
                             ]
                           : []),
                     ].map(([label, value]) => (
                        <div key={label}>
                           <dt className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                              {label}
                           </dt>
                           <dd className="mt-0.5 text-gray-900 dark:text-gray-100 break-words">
                              {value || (
                                 <span className="text-gray-400 dark:text-gray-600">
                                    Not provided
                                 </span>
                              )}
                           </dd>
                        </div>
                     ))}
                  </dl>
                  <div>
                     <p className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        Message
                     </p>
                     <p className="mt-0.5 text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words">
                        {selected.message}
                     </p>
                  </div>
                  <SearchableSelect
                     label="Status"
                     searchable={false}
                     options={STATUS_OPTIONS}
                     value={selected.status || 'NEW'}
                     onChange={(value) => handleStatusChange(selected.id, value)}
                     className="!mb-0"
                  />
                  <div className="flex justify-end gap-3 pt-2">
                     <Button variant="danger" onClick={() => setDeleteTarget(selected)}>
                        Delete
                     </Button>
                     <Button variant="secondary" onClick={() => setSelectedId(null)}>
                        Close
                     </Button>
                  </div>
               </div>
            )}
         </Modal>

         <ConfirmModal
            isOpen={!!deleteTarget}
            title="Delete Message"
            message={`Are you sure you want to delete this message from "${deleteTarget?.email}"? This cannot be undone.`}
            confirmLabel="Delete"
            confirmVariant="danger"
            onConfirm={handleDelete}
            onCancel={() => setDeleteTarget(null)}
            loading={!!deletingId}
         />
      </PanelLayout>
   );
};

export default ContactMessages;
